import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2, RefreshCw, Shield, Mail, AlertCircle } from 'lucide-react';

interface OtpVerificationFormProps {
  email: string;
  onVerify: (token: string) => Promise<void>;
  onResend?: () => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  title?: string;
  subtitle?: string;
  type?: 'signup' | 'recovery' | 'email_change' | 'email';
  autoFocus?: boolean;
}

export function OtpVerificationForm({
  email,
  onVerify,
  onResend,
  isLoading = false,
  error,
  title = "Verify Your Email",
  subtitle = "Enter the 6-digit code sent to your email",
  type = 'email',
  autoFocus = true
}: OtpVerificationFormProps) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isResending, setIsResending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first input
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value.slice(-1); // Only take the last digit
    setCode(newCode);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (newCode.every(digit => digit) && newCode.join('').length === 6) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (token: string) => {
    if (token.length === 6) {
      try {
        await onVerify(token);
      } catch {
        // Error handling is done in parent component
      }
    }
  };

  const handleResend = async () => {
    if (!onResend || resendCountdown > 0) return;
    
    setIsResending(true);
    try {
      await onResend();
      setResendCountdown(60); // 60 second cooldown
      setCode(['', '', '', '', '', '']); // Clear form
      inputRefs.current[0]?.focus();
    } catch {
      // Error handling is done in parent component
    } finally {
      setIsResending(false);
    }
  };

  const getTypeIcon = () => {
    switch (type) {
      case 'signup':
        return <Shield className="w-5 h-5" />;
      case 'recovery':
        return <RefreshCw className="w-5 h-5" />;
      default:
        return <Mail className="w-5 h-5" />;
    }
  };

  const getMaskedEmail = (email: string) => {
    const [username, domain] = email.split('@');
    if (username.length <= 2) return email;
    const masked = username.slice(0, 2) + '*'.repeat(username.length - 2);
    return `${masked}@${domain}`;
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full mb-4">
          <div className="text-purple-600">
            {getTypeIcon()}
          </div>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-600 mb-2">{subtitle}</p>
        <p className="text-sm text-gray-500">
          Sent to <span className="font-medium">{getMaskedEmail(email)}</span>
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <div className="flex justify-center space-x-3 mb-4">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              value={digit}
              onChange={e => handleInputChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                error 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-200'
              } ${digit ? 'bg-purple-50 border-purple-300' : 'bg-white'}`}
              maxLength={1}
              disabled={isLoading}
            />
          ))}
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="flex items-center justify-center space-x-2 text-red-600 text-sm mb-4"
          >
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </motion.div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center space-x-2 text-gray-600 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Verifying code...</span>
          </div>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-center"
      >
        <p className="text-sm text-gray-600 mb-4">
          Didn't receive the code?
        </p>
        
        <button
          onClick={handleResend}
          disabled={!onResend || resendCountdown > 0 || isResending}
          className={`inline-flex items-center space-x-2 text-sm font-medium transition-colors ${
            resendCountdown > 0 || isResending
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          {isResending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Sending...</span>
            </>
          ) : resendCountdown > 0 ? (
            <span>Resend in {resendCountdown}s</span>
          ) : (
            <>
              <RefreshCw className="w-4 h-4" />
              <span>Resend Code</span>
            </>
          )}
        </button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg"
      >
        <div className="flex items-start space-x-3">
          <div className="text-blue-600">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-blue-900">Security Notice</h4>
            <p className="text-sm text-blue-700 mt-1">
              This code expires in 10 minutes and can only be used once. Never share it with anyone.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}